import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const FEATURES = [
  {
    title: "Designed for movement",
    body: "A breathable, performance-grade compression knit that flexes with every step, stretch, and stride.",
  },
  {
    title: "Founder-led by a physiotherapist",
    body: "Created by Dr Shivam Jhatta PT — someone who understands recovery science and what people actually want to wear.",
  },
  {
    title: "Built to be worn, not hidden",
    body: "Considered lines and soft neutral tones that belong in your wardrobe, not your drawer.",
  },
];

const COLORWAY_IMGS = [
  { src: "/renders/knee-mocha.png", alt: "LIGATEN knee sleeve in mocha" },
  { src: "/renders/knee-taupe.png", alt: "LIGATEN knee sleeve in taupe" },
  { src: "/renders/knee-beige.png", alt: "LIGATEN knee sleeve in beige" },
];

export const Spotlight = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const o0 = useTransform(scrollYProgress, [0.3, 0.46], [1, 0]);
  const o1 = useTransform(scrollYProgress, [0.34, 0.48, 0.58, 0.72], [0, 1, 1, 0]);
  const o2 = useTransform(scrollYProgress, [0.62, 0.78], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.96, 1.05]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-1.5, 1.5]);
  const opacities = [o0, o1, o2];

  return (
    <section ref={ref} data-testid="spotlight-section" className="bg-[#E5DFD5]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
        <Reveal className="md:col-span-6">
          <div className="rounded-2xl border border-[#2C241E]/15 bg-[#EFE8DC] p-4 md:p-6">
            <p className="text-[10px] font-medium tracking-[0.3em] uppercase text-[#2C241E]/50 mb-4">
              Fig. 01 / Knee Sleeve
            </p>
            <div className="relative rounded-xl overflow-hidden aspect-[4/5]">
              {COLORWAY_IMGS.map((img, i) => (
                <motion.img
                  key={img.src}
                  data-testid={i === 0 ? "spotlight-image" : `spotlight-image-${i + 1}`}
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  style={{ opacity: opacities[i], scale, rotate }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ))}
            </div>
            <p
              data-testid="spotlight-caption"
              className="text-[10px] font-medium tracking-[0.3em] uppercase text-[#2C241E]/50 mt-4 text-center"
            >
              Mocha · Taupe · Beige — Scroll to Shift Tone
            </p>
          </div>
        </Reveal>
        <div className="md:col-span-6 md:pl-6">
          <Reveal>
            <p className="text-xs font-medium tracking-[0.25em] uppercase text-[#2C241E]/50 mb-4">
              03 / Why Ligaten
            </p>
            <h2
              data-testid="spotlight-headline"
              className="font-display text-4xl md:text-6xl tracking-tighter leading-[1.05] text-[#2C241E]"
            >
              Built Different. Worn Daily.
            </h2>
          </Reveal>
          <div className="mt-12">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={0.1 + i * 0.08}>
                <div
                  data-testid={`spotlight-feature-${i + 1}`}
                  className="border-t border-[#2C241E]/15 py-6"
                >
                  <h3 className="font-display text-xl md:text-2xl text-[#2C241E]">{f.title}</h3>
                  <p className="mt-2 text-sm md:text-base leading-relaxed text-[#2C241E]/65">
                    {f.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
